import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Popover, Space, Switch, Typography, message } from 'antd'
import { SoundOutlined } from '@ant-design/icons'

function saved(key: string, fallback: boolean) {
  try { const value = localStorage.getItem(key); return value === null ? fallback : value === 'true' } catch { return fallback }
}

// User interaction unlocks audio; notification permission is requested only by a click.
export function useMessageAlerts() {
  const [sound, setSound] = useState(() => saved('mall-alert-sound', true))
  const [desktop, setDesktop] = useState(() => saved('mall-alert-desktop', false))
  const [ready, setReady] = useState(false)
  useEffect(() => {
    try { localStorage.setItem('mall-alert-sound', String(sound)); localStorage.setItem('mall-alert-desktop', String(desktop)) } catch { /* Storage may be disabled. */ }
  }, [sound, desktop])
  const soundRef = useRef(sound)
  const desktopRef = useRef(desktop)
  soundRef.current = sound
  desktopRef.current = desktop
  const audio = useRef<AudioContext | null>(null)
  const notices = useRef(new Set<Notification>())
  const lastSound = useRef(0)
  const unlock = useCallback(async () => {
    try {
      audio.current ??= new AudioContext()
      await audio.current.resume()
      setReady(audio.current.state === 'running')
    } catch { setReady(false) }
  }, [])
  useEffect(() => {
    const interact = () => { void unlock() }
    document.addEventListener('pointerdown', interact)
    document.addEventListener('keydown', interact)
    return () => {
      document.removeEventListener('pointerdown', interact)
      document.removeEventListener('keydown', interact)
      void audio.current?.close().catch(() => {})
      audio.current = null
      notices.current.forEach(n => n.close())
      notices.current.clear()
    }
  }, [unlock])
  const beep = useCallback(() => {
    const ctx = audio.current
    if (!ctx || ctx.state !== 'running' || Date.now() - lastSound.current < 800) return
    lastSound.current = Date.now()
    try {
      const gain = ctx.createGain()
      const oscillator = ctx.createOscillator()
      oscillator.frequency.value = 880
      gain.gain.setValueAtTime(0.12, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25)
      oscillator.connect(gain)
      gain.connect(ctx.destination)
      oscillator.start()
      oscillator.stop(ctx.currentTime + 0.26)
      oscillator.onended = () => { oscillator.disconnect(); gain.disconnect() }
    } catch { /* Audio failure must not affect chat. */ }
  }, [])
  const notify = useCallback((title: string, body: string, tag: string, open: () => void) => {
    if (soundRef.current) beep()
    if (!desktopRef.current || !window.isSecureContext || !('Notification' in window) || Notification.permission !== 'granted') return
    try {
      const n = new Notification(title, { body, tag, silent: true })
      notices.current.add(n)
      n.onclose = () => notices.current.delete(n)
      n.onclick = () => { window.focus(); n.close(); open() }
    } catch { /* Some mobile browsers do not support the constructor. */ }
  }, [beep])
  const enableDesktop = async (enabled: boolean) => {
    if (!enabled) { setDesktop(false); return }
    if (!window.isSecureContext || !('Notification' in window)) {
      message.info('当前浏览器不支持系统通知，请使用 HTTPS 和支持通知的桌面浏览器'); return
    }
    try {
      const permission = await Notification.requestPermission()
      setDesktop(permission === 'granted')
      if (permission !== 'granted') message.info('系统通知未开启，可在浏览器网站设置中允许通知')
    } catch { message.info('系统通知暂不可用，声音提醒仍可使用') }
  }
  const controls = <Popover trigger="click" title="新消息提醒" content={<Space direction="vertical">
    <Space>声音提醒<Switch checked={sound} onChange={v => { setSound(v); if (v) void unlock() }} /></Space>
    <Space>系统通知<Switch checked={desktop} onChange={v => { void enableDesktop(v) }} /></Space>
    <Button size="small" onClick={async () => { await unlock(); beep() }}>试听提示音</Button>
    <Typography.Text type="secondary">{ready ? '声音已就绪' : '点击页面后启用声音'}</Typography.Text>
    <Typography.Text type="secondary">保持后台页面打开，系统通知需浏览器授权。</Typography.Text>
  </Space>}><Button type="text" icon={<SoundOutlined />}>消息提醒</Button></Popover>
  return { notify, controls }
}
